/**
 * <featured-product-showcase> custom element
 * Desktop: flex accordion with drag scroll + arrow navigation
 * Mobile: CSS scroll-snap with touch scroll + dot pagination
 */
if (!customElements.get('featured-product-showcase')) {
  class FeaturedProductShowcase extends HTMLElement {
    constructor() {
      super();
      this._isDragging = false;
      this._dragMoved = false;
      this._dragStartX = 0;
      this._dragScrollLeft = 0;
      this._scrollTimer = null;
      this._activeIndex = 0;
    }

    connectedCallback() {
      this.viewport = this.querySelector('.fps__viewport');
      this.track = this.querySelector('.fps__track');
      this.cards = Array.from(this.querySelectorAll('.fps__card'));
      this.dots = Array.from(this.querySelectorAll('.fps__dot'));
      this.prevBtn = this.querySelector('.fps__arrow--prev');
      this.nextBtn = this.querySelector('.fps__arrow--next');
      this.total = this.cards.length;
      this._originalTotal = this.total;

      if (!this.viewport || !this.track || this.total === 0) return;

      this._interactionMode = this.dataset.interactionMode || 'click';
      this._loopEnabled = this.dataset.enableLoop === 'true' && this._originalTotal > 1;
      this._loopOffset = 0;
      this._rebasing = false;

      if (this._loopEnabled) {
        this._setupLoop();
        this._activeIndex = this._originalTotal;
      }

      this._bindEvents();
      this._updateArrows();
    }

    _setupLoop() {
      var originals = this.cards.slice();
      var frag = document.createDocumentFragment();
      var before = document.createDocumentFragment();

      originals.forEach(function (card) {
        var cloneAfter = card.cloneNode(true);
        cloneAfter.classList.remove('fps__card--active');
        cloneAfter.setAttribute('data-clone', 'true');
        cloneAfter.removeAttribute('data-shopify-editor-block');
        frag.appendChild(cloneAfter);

        var cloneBefore = card.cloneNode(true);
        cloneBefore.classList.remove('fps__card--active');
        cloneBefore.setAttribute('data-clone', 'true');
        cloneBefore.removeAttribute('data-shopify-editor-block');
        before.appendChild(cloneBefore);
      });

      this.track.appendChild(frag);
      this.track.insertBefore(before, this.track.firstChild);

      this.cards = Array.from(this.querySelectorAll('.fps__card'));

      var self = this;
      requestAnimationFrame(function () {
        self._computeLoopOffset();
        self._centerOnOriginals(false);
      });
    }

    _computeLoopOffset() {
      if (!this._loopEnabled || this._originalTotal === 0) return;
      var originals = this.cards.slice(this._originalTotal, this._originalTotal * 2);
      if (originals.length === 0) return;
      var first = originals[0];
      var last = originals[originals.length - 1];
      this._loopOffset = (last.offsetLeft + last.offsetWidth) - first.offsetLeft
        + this._getTrackGap();
    }

    _getTrackGap() {
      var style = window.getComputedStyle(this.track);
      var gap = parseFloat(style.columnGap || style.gap || '0');
      return isNaN(gap) ? 0 : gap;
    }

    _centerOnOriginals(smooth) {
      if (!this._loopEnabled) return;
      var firstOriginal = this.cards[this._originalTotal];
      if (!firstOriginal) return;
      this._rebasing = true;
      this.viewport.scrollTo({
        left: firstOriginal.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });
      var self = this;
      requestAnimationFrame(function () { self._rebasing = false; });
    }

    disconnectedCallback() {
      this._unbindEvents();
    }

    _bindEvents() {
      this._handleTrackClick = this._onTrackClick.bind(this);
      this._handlePrev = this._onPrev.bind(this);
      this._handleNext = this._onNext.bind(this);
      this._handleDotClick = this._onDotClick.bind(this);
      this._handleScroll = this._onScroll.bind(this);
      this._handleResize = this._onResize.bind(this);
      this._handleMouseDown = this._onMouseDown.bind(this);
      this._handleMouseMove = this._onMouseMove.bind(this);
      this._handleMouseUp = this._onMouseUp.bind(this);
      this._handleMouseLeave = this._onMouseLeave.bind(this);
      this._handleWheel = this._onWheel.bind(this);

      this.track.addEventListener('click', this._handleTrackClick);
      if (this._interactionMode === 'hover') {
        this._handleCardMouseEnter = this._onCardMouseEnter.bind(this);
        this._handleCardMouseLeave = this._onCardMouseLeave.bind(this);
        this.cards.forEach(function (card) {
          card.addEventListener('mouseenter', this._handleCardMouseEnter);
          card.addEventListener('mouseleave', this._handleCardMouseLeave);
        }, this);
      }
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._handleNext);
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', this._handleDotClick);
      }, this);
      this.viewport.addEventListener('scroll', this._handleScroll, { passive: true });
      window.addEventListener('resize', this._handleResize);
      this.viewport.addEventListener('mousedown', this._handleMouseDown);
      this.viewport.addEventListener('mousemove', this._handleMouseMove);
      this.viewport.addEventListener('mouseup', this._handleMouseUp);
      this.viewport.addEventListener('mouseleave', this._handleMouseLeave);
      this.viewport.addEventListener('wheel', this._handleWheel, { passive: false });
    }

    _unbindEvents() {
      if (this.track) this.track.removeEventListener('click', this._handleTrackClick);
      if (this._handleCardMouseEnter) {
        this.cards.forEach(function (card) {
          card.removeEventListener('mouseenter', this._handleCardMouseEnter);
          card.removeEventListener('mouseleave', this._handleCardMouseLeave);
        }, this);
      }
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._handleNext);
      this.dots.forEach(function (dot) {
        dot.removeEventListener('click', this._handleDotClick);
      }, this);
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this._handleScroll);
        this.viewport.removeEventListener('mousedown', this._handleMouseDown);
        this.viewport.removeEventListener('mousemove', this._handleMouseMove);
        this.viewport.removeEventListener('mouseup', this._handleMouseUp);
        this.viewport.removeEventListener('mouseleave', this._handleMouseLeave);
        this.viewport.removeEventListener('wheel', this._handleWheel);
      }
      window.removeEventListener('resize', this._handleResize);
    }

    get _isDesktop() {
      return window.innerWidth >= 750;
    }

    /* ── Mouse drag ── */

    _onMouseDown(e) {
      if (e.button !== 0) return;
      this._isDragging = true;
      this._dragMoved = false;
      this._dragStartX = e.pageX;
      this._dragLastX = e.pageX;
      this._dragScrollLeft = this.viewport.scrollLeft;
    }

    _onMouseMove(e) {
      if (!this._isDragging) return;
      e.preventDefault();
      var dx = e.pageX - this._dragStartX;
      if (Math.abs(dx) > 5) {
        this._dragMoved = true;
        this.viewport.classList.add('fps__viewport--dragging');
      }
      this._dragLastX = e.pageX;
      this.viewport.scrollLeft = this._dragScrollLeft - dx;
    }

    _onMouseUp() {
      this._stopDrag();
    }

    _onMouseLeave() {
      this._stopDrag();
    }

    _stopDrag() {
      if (!this._isDragging) return;
      this._isDragging = false;
      this.viewport.classList.remove('fps__viewport--dragging');
      var wasMoved = this._dragMoved;
      var dragDirection = (this._dragLastX || 0) - (this._dragStartX || 0);

      if (wasMoved) {
        this._snapToNearestCard(dragDirection);
        var self = this;
        setTimeout(function () { self._dragMoved = false; }, 50);
      }
    }

    /* ── Wheel / trackpad ── */

    _onWheel(e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
      }
    }

    _snapToNearestCard(dragDirection) {
      if (!this.cards || this.cards.length === 0) return;
      var vpLeft = this.viewport.scrollLeft;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var i = 0; i < this.cards.length; i++) {
        var d = Math.abs(this.cards[i].offsetLeft - vpLeft);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }

      var nearest = this.cards[bestIdx];
      var bias = this._getCollapsedCardStep() * 0.15;
      if (dragDirection < 0 && nearest.offsetLeft < vpLeft - bias && bestIdx < this.cards.length - 1) {
        bestIdx += 1;
      } else if (dragDirection > 0 && nearest.offsetLeft > vpLeft + bias && bestIdx > 0) {
        bestIdx -= 1;
      }

      var target = this.cards[bestIdx];
      if (!target) return;
      this.viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    /* ── Card hover (accordion) ── */

    _onCardMouseEnter(e) {
      if (!this._isDesktop) return;
      var index = parseInt(e.currentTarget.dataset.index, 10);
      if (isNaN(index)) return;
      var cardEl = e.currentTarget;
      var self = this;
      clearTimeout(this._hoverTimer);
      this._hoverTimer = setTimeout(function () { self._setActive(index, cardEl); }, 250);
    }

    _onCardMouseLeave() {
      clearTimeout(this._hoverTimer);
    }

    /* ── Card click (accordion) ── */

    _onTrackClick(e) {
      if (this._dragMoved) return;
      if (this._interactionMode === 'hover' && this._isDesktop) return;

      var cardEl = e.target.closest('.fps__card');
      if (!cardEl || !this.contains(cardEl)) return;
      if (e.target.closest('.fps__btn')) return;

      var logicalIdx = parseInt(cardEl.dataset.index, 10);
      if (isNaN(logicalIdx) || !this._isDesktop) return;

      this._setActive(logicalIdx, cardEl);
    }

    _setActive(logicalIndex, cardEl) {
      var cardPos;
      if (cardEl) {
        cardPos = this.cards.indexOf(cardEl);
      } else if (this._loopEnabled) {
        cardPos = this._originalTotal + logicalIndex;
      } else {
        cardPos = logicalIndex;
      }
      if (cardPos < 0 || cardPos >= this.cards.length) return;
      if (cardPos === this._activeIndex) return;

      var oldCard = this.cards[this._activeIndex];
      var vpLeft = this.viewport.scrollLeft;
      var wasLeft = oldCard && oldCard.offsetLeft + oldCard.offsetWidth <= vpLeft;
      var oldWidth = wasLeft ? oldCard.offsetWidth : 0;

      this.cards.forEach(function (c) { c.classList.remove('fps__card--active'); });
      this.cards[cardPos].classList.add('fps__card--active');
      this._activeIndex = cardPos;

      if (wasLeft && oldCard) {
        var vp = this.viewport;
        requestAnimationFrame(function () {
          var newWidth = oldCard.offsetWidth;
          vp.scrollLeft += (newWidth - oldWidth);
        });
      }

      var dotIdx = this._loopEnabled
        ? ((logicalIndex % this._originalTotal) + this._originalTotal) % this._originalTotal
        : cardPos;
      this._updateDots(dotIdx);
    }

    /* ── Arrow navigation (one card per move) ── */

    _onPrev() {
      this._stepTo(-1);
    }

    _onNext() {
      this._stepTo(1);
    }

    _stepTo(direction) {
      if (!this.cards || this.cards.length === 0) return;

      var step = this._getCollapsedCardStep();
      if (step <= 0) return;

      this.viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    _getCollapsedCardStep() {
      var collapsed = null;
      for (var i = 0; i < this.cards.length; i++) {
        if (!this.cards[i].classList.contains('fps__card--active')) {
          collapsed = this.cards[i];
          break;
        }
      }
      if (!collapsed) collapsed = this.cards[0];
      if (!collapsed) return 0;
      return collapsed.offsetWidth + this._getTrackGap();
    }

    _updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      if (this._loopEnabled) {
        this.prevBtn.disabled = false;
        this.nextBtn.disabled = false;
        return;
      }
      var vp = this.viewport;
      this.prevBtn.disabled = vp.scrollLeft <= 1;
      this.nextBtn.disabled = vp.scrollLeft + vp.offsetWidth >= vp.scrollWidth - 1;
    }

    /* ── Scroll handler (unified desktop + mobile) ── */

    _onScroll() {
      if (this._loopEnabled && !this._rebasing) {
        this._maybeRebase();
      }
      var self = this;
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(function () {
        if (self._rebasing) return;
        self._updateArrows();
        if (!self._isDesktop) {
          self._syncMobileDot();
        }
      }, 80);
    }

    _maybeRebase() {
      if (!this._loopOffset) return;
      var vp = this.viewport;
      var left = vp.scrollLeft;
      var vpWidth = vp.offsetWidth;
      var upperBound = this._loopOffset * 2 - vpWidth;

      if (left < this._loopOffset * 0.5) {
        this._rebasing = true;
        vp.scrollLeft = left + this._loopOffset;
        var self = this;
        requestAnimationFrame(function () { self._rebasing = false; });
      } else if (left > upperBound + this._loopOffset * 0.5) {
        this._rebasing = true;
        vp.scrollLeft = left - this._loopOffset;
        var self2 = this;
        requestAnimationFrame(function () { self2._rebasing = false; });
      }
    }

    _onResize() {
      if (this._loopEnabled) {
        clearTimeout(this._resizeTimer);
        var self = this;
        this._resizeTimer = setTimeout(function () {
          self._computeLoopOffset();
          self._centerOnOriginals(false);
          self._updateArrows();
        }, 100);
        return;
      }
      this._updateArrows();
    }

    _syncMobileDot() {
      var scrollLeft = this.viewport.scrollLeft;
      var vpWidth = this.viewport.offsetWidth;
      if (vpWidth === 0) return;

      var idx = Math.round(scrollLeft / vpWidth);
      idx = Math.max(0, Math.min(idx, this.cards.length - 1));
      var dotIdx = this._loopEnabled ? idx % this._originalTotal : idx;
      this._updateDots(dotIdx);
    }

    /* ── Dots ── */

    _onDotClick(e) {
      var idx = parseInt(e.currentTarget.dataset.dotIndex, 10);
      if (isNaN(idx)) return;

      var cardPos = this._loopEnabled ? this._originalTotal + idx : idx;
      var card = this.cards[cardPos];
      if (!card) return;

      if (this._isDesktop) this._setActive(idx, card);
      this.viewport.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }

    _updateDots(activeIdx) {
      this.dots.forEach(function (dot, i) {
        var on = i === activeIdx;
        dot.classList.toggle('fps__dot--active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    /* ── Shopify editor integration ── */

    selectBlock(blockId) {
      var card = this.querySelector('[data-shopify-editor-block="' + blockId + '"]');
      if (!card) return;
      var index = parseInt(card.dataset.index, 10);
      if (isNaN(index)) return;
      if (this._isDesktop) this._setActive(index, card);
      this.viewport.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }
  }

  customElements.define('featured-product-showcase', FeaturedProductShowcase);
}

document.addEventListener('shopify:block:select', function (e) {
  var el = e.target.closest('featured-product-showcase');
  if (el && typeof el.selectBlock === 'function') {
    el.selectBlock(e.detail.blockId);
  }
});
